/**
 * PRESENTATION-ONLY knowledge about reports.
 *
 * Everything in this file is about how the Reports workspace LOOKS and how a report is FOUND. None
 * of it reaches the server: the filter keys, values, period and request bodies are exactly what the
 * catalogue declares and what the API has always received. Splitting a report's filters into a
 * primary row and an advanced drawer, listing search aliases, and remembering which reports someone
 * opened recently are all decisions a client is allowed to make on its own.
 *
 * If a rule here ever needs a fact only the server has, the rule is wrong — not the server.
 */
import type { ReportDefinition, ReportFilterDef } from './reportKit';

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Finding a report by the words people actually use
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * Extra words that should find a report, beyond its own name, category and description.
 *
 * Purely a search index: nobody sees these, and adding one can only make a report easier to reach.
 * They exist because people search for the thing they want an answer about ("dealer balance",
 * "who owes us"), not for the title somebody gave the report.
 */
export const SEARCH_ALIASES: Record<string, string[]> = {
  SALES_BY_DAY: ['daily', 'trend', 'turnover', 'revenue', 'orders per day'],
  SALES_BY_RETAILER: ['dealer', 'customer', 'account', 'who bought', 'top dealers'],
  SALES_BY_PRODUCT: ['sku', 'item', 'product mix', 'what sold'],
  SALES_BY_TERRITORY: ['region', 'state', 'district', 'beat', 'area', 'zone'],
  SALES_BY_SALESPERSON: ['rep', 'staff', 'field', 'performance', 'team'],
  SALES_BY_CHANNEL: ['web', 'app', 'source', 'mix'],
  COLLECTION_BY_DAY: ['receipts', 'money in', 'cash received', 'daily collection'],
  COLLECTION_BY_MODE: ['cash', 'upi', 'cheque', 'neft', 'bank', 'payment method'],
  OUTSTANDING: ['ageing', 'aging', 'debtors', 'receivables', 'who owes', 'dues', 'balance', 'credit'],
  OVERDUE_INVOICES: ['late', 'unpaid', 'past due', 'receivables', 'bills'],
  RETAILER_LEDGER: ['dealer', 'statement', 'account', 'balance', 'passbook', 'khata', 'debit', 'credit'],
  PAYMENT_REGISTER: ['receipts', 'money in', 'cash book', 'collections'],
  CREDIT_NOTE_REGISTER: ['debit note', 'return', 'adjustment', 'cn', 'dn'],
  STOCK_BALANCE: ['inventory', 'on hand', 'godown', 'warehouse', 'quantity'],
  EXPIRY_ALERT: ['shelf life', 'expiring', 'near expiry', 'batch'],
  REORDER_ALERT: ['low stock', 'replenish', 'purchase', 'out of stock'],
  STOCK_VALUATION: ['inventory value', 'closing stock', 'worth', 'valuation'],
  STOCK_TRANSFERS: ['movement', 'godown transfer', 'dispatch', 'inter-warehouse'],
  ATTENDANCE_SUMMARY: ['present', 'absent', 'muster', 'days worked', 'field staff'],
  ANOMALY_REVIEW: ['exception', 'flags', 'suspicious', 'gps', 'fraud'],
  VISIT_SUMMARY: ['calls', 'beat', 'coverage', 'field visits'],
  ROUTE_COMPLIANCE: ['beat plan', 'adherence', 'journey plan', 'pjp'],
  COMPLAINT_AGING: ['tickets', 'service', 'grievance', 'open complaints'],
  COMPLAINT_BY_PRODUCT: ['quality', 'defect', 'product issues'],
  SLA_BREACH: ['late', 'overdue tickets', 'escalation', 'service level'],
  RETAILER_INACTIVE: ['dormant', 'lapsed', 'lost', 'no orders', 'churn'],
  NEW_RETAILERS: ['onboarded', 'added', 'new dealers', 'acquisition'],
  GST_SUMMARY: ['tax', 'gstr', 'returns', 'cgst', 'sgst', 'igst'],
  INVOICE_VERIFICATION: ['tally', 'bills', 'verified', 'invoice check'],
};

/** Everything a report can be found by, lower-cased once per report. */
export const searchIndex = (r: ReportDefinition): string =>
  `${r.name} ${r.category} ${r.description} ${(SEARCH_ALIASES[r.code] ?? []).join(' ')}`.toLowerCase();

/**
 * Does this report match what was typed?
 *
 * Every WORD has to match something, so "dealer balance" narrows to reports that are about both
 * rather than to everything mentioning either — two words should not return more rows than one.
 */
export function matchesQuery(r: ReportDefinition, query: string, index?: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const hay = index ?? searchIndex(r);
  return q.split(/\s+/).every((w) => hay.includes(w));
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Which filters belong on the bar, and which belong in the drawer
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/** At most this many report filters sit on the parameter bar; the rest move to the drawer. */
const MAX_INLINE = 3;

/**
 * Reports whose most-used filter is not simply the first one the catalogue declares.
 *
 * Kept deliberately short. The generic rule below is right for almost every report, and a long list
 * of hand-tuned exceptions is a list that goes stale the moment a filter is added on the server.
 */
const PRIMARY_OVERRIDE: Record<string, string[]> = {
  // A ledger is read one account at a time; the territory is how you find the account, not the point.
  RETAILER_LEDGER: ['retailerId', 'txnType'],
  // An aging list is read by bucket far more often than by rep.
  OUTSTANDING: ['territoryId', 'bucket'],
  // The queue is worked by severity and status; the category is a refinement.
  COMPLAINT_AGING: ['severity', 'complaintStatus'],
  SLA_BREACH: ['severity', 'assignedTo'],
  // Stock is looked up per godown, then per product family.
  STOCK_BALANCE: ['warehouseId', 'productGroup'],
  STOCK_VALUATION: ['warehouseId', 'productGroup'],
  EXPIRY_ALERT: ['warehouseId', 'expiredOnly'],
  REORDER_ALERT: ['warehouseId', 'outOfStockOnly'],
};

/**
 * Split a report's filters into the ones on the bar and the ones behind "More filters".
 *
 * The rule, in order:
 *   1. anything the catalogue gives a DEFAULT is primary — it is already filtering the report, and a
 *      filter that is in force while hidden in a drawer is how a reader ends up mistrusting a number;
 *   2. then the report's own override, if it has one;
 *   3. then the first filters the catalogue declares, which is the order its author put them in.
 *
 * Nothing is dropped: primary + advanced is always the complete list the server declared.
 */
export function splitFilters(def?: ReportDefinition): { primary: ReportFilterDef[]; advanced: ReportFilterDef[] } {
  const all = def?.filters ?? [];
  if (!all.length) return { primary: [], advanced: [] };

  const wanted: string[] = [];
  for (const f of all) if (f.default !== undefined) wanted.push(f.key);
  for (const k of PRIMARY_OVERRIDE[def?.code ?? ''] ?? []) if (!wanted.includes(k)) wanted.push(k);
  for (const f of all) {
    if (wanted.length >= MAX_INLINE) break;
    if (!wanted.includes(f.key)) wanted.push(f.key);
  }
  const primaryKeys = new Set(wanted.slice(0, MAX_INLINE));
  return {
    primary: all.filter((f) => primaryKeys.has(f.key)),
    advanced: all.filter((f) => !primaryKeys.has(f.key)),
  };
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Reading a filter value back out for the chips
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/** Is this value one the reader actually chose, rather than the state the control opens in? */
export function isSetValue(v: any): boolean {
  if (v === undefined || v === null || v === '') return false;
  return !(Array.isArray(v) && v.length === 0);
}

/**
 * The value in the reader's words — "Shree Ganesh Krishi Kendra", not "1415".
 *
 * A chip that prints the id is not a chip, it is a puzzle. Falls back to the raw value for a text or
 * number filter, which is already what a person typed.
 */
export function filterValueLabel(filter: ReportFilterDef, value: any): string {
  const one = (v: any) => filter.options?.find((o) => String(o.value) === String(v))?.label ?? String(v);
  if (Array.isArray(value)) {
    const labels = value.map(one);
    return labels.length <= 2 ? labels.join(', ') : `${labels[0]} +${labels.length - 1}`;
  }
  return one(value);
}

/* ─────────────────────────────────────────────────────────────────────────────────────────────
 * Recently opened reports — this browser only
 * ───────────────────────────────────────────────────────────────────────────────────────────── */

/**
 * Stored in localStorage under the app's own `gb:` prefix, like the table column choices.
 *
 * Deliberately NOT sent anywhere: a list of report codes is a navigation convenience, and giving it
 * a server would mean a table, an endpoint and a privacy question for something a browser can hold.
 * Every read is defensive — a cleared or corrupted store returns nothing rather than breaking the
 * catalogue, which must work perfectly without this.
 */
const RECENT_KEY = 'gb:reports:recent';
const RECENT_MAX = 6;

export function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((x) => typeof x === 'string').slice(0, RECENT_MAX) : [];
  } catch { return []; }
}

export function pushRecent(code: string): string[] {
  const next = [code, ...readRecent().filter((c) => c !== code)].slice(0, RECENT_MAX);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* private mode: recents are a convenience */ }
  return next;
}
